import type { SSEEvent } from "@/app/lib/types";

export type LiveTextEvent = Extract<
  SSEEvent,
  {
    type:
      | "delta"
      | "narration_boundary"
      | "citations_replace"
      | "done"
      | "error";
  }
>;

export interface StreamPresentation {
  text: string;
  /** Ephemeral narration shown above a provider's live answer. */
  narrationStatus?: string;
}

/**
 * Applies text-related live SSE events to presentation state only. Callers
 * keep this state separate from the response objects they persist.
 */
export function transitionStreamPresentation<T extends StreamPresentation>(
  responses: Record<string, T>,
  event: LiveTextEvent
): Record<string, T> {
  const current = responses[event.provider];
  if (!current) return responses;

  switch (event.type) {
    case "delta":
      return {
        ...responses,
        [event.provider]: {
          ...current,
          text: current.text + event.delta,
          narrationStatus: undefined,
        },
      };
    case "narration_boundary":
      return {
        ...responses,
        [event.provider]: {
          ...current,
          text: "",
          narrationStatus: current.text || undefined,
        },
      };
    case "citations_replace":
      return {
        ...responses,
        [event.provider]: {
          ...current,
          text: event.text,
          narrationStatus: undefined,
        },
      };
    case "done":
    case "error":
      // A terminal event can immediately follow a search narration with no
      // answer text, so clear the transient status for this provider either way.
      return {
        ...responses,
        [event.provider]: {
          ...current,
          narrationStatus: undefined,
        },
      };
  }
}
